import { useState, useEffect } from "react";
import { FaCheck, FaRegEdit, FaRegEye } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import DynamicSelect from "../../DynamicSelect";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import TableSkeleton from "../../TableSkeleton";
import Pagination from "../../Shared/Pagination";

const DoctorTable = ({
  data,
  columns,
  itemsPerPage = 10,
  selectedLanguage,
  searchTerm,
  handleSearch,
  handleEdit,
  handleAdd,
  departments,
  openDeleteModal,
  loading, // Add loading prop
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState({
    value: "all",
    label: "All",
  });
  const [selectedSort, setSelectedSort] = useState({
    value: "default",
    label: "Default",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [additionOrder, setAdditionOrder] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // Define skeleton columns to match the table structure
  const skeletonColumns = [
    { width: 40, height: 20, className: "pl-3" }, // Checkbox
    { width: 60, height: 20, className: "pl-5" }, // SL No
    { width: 80, height: 20 }, // id
    { width: 200, height: 20 }, // name (includes image)
    { width: 150, height: 20 }, // specialty
    { width: 120, height: 20 }, // phone number
    { width: 150, height: 20 }, // email
    { width: 200, height: 20 }, // schedule
    { width: 100, height: 20 }, // years of experience
    { width: 120, height: 20, className: "text-center" },
  ];

  // Sync state with data changes
  useEffect(() => {
    setSelectedRows(new Array(data.length).fill(false));
    const existingIds = new Set(additionOrder);
    const newIds = data
      .filter((doctor) => !existingIds.has(doctor.id))
      .map((doctor) => doctor.id);
    setAdditionOrder((prev) => [...prev, ...newIds]);
  }, [data]);

  // Update currentPage when data.length changes
  useEffect(() => {
    if (isLoading) return;
    const newTotalPages = Math.ceil(data.length / itemsPerPage);
    if (data.length > 0 && currentPage > newTotalPages) {
      setCurrentPage(newTotalPages);
    }
  }, [data.length, itemsPerPage, isLoading]);

  // Update header checkbox
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, data.length);
    const currentPageRows = selectedRows.slice(startIndex, endIndex);
    setIsHeaderChecked(
      currentPageRows.length > 0 &&
        currentPageRows.every((selected) => selected)
    );
  }, [selectedRows, currentPage, itemsPerPage, data.length]);

  useEffect(() => {
    setTotalRecords(data.length);
  }, [data.length]);

  const handleHeaderCheckboxChange = () => {
    const newSelectedRows = [...selectedRows];
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, data.length);
    const areAllSelected = selectedRows
      .slice(startIndex, endIndex)
      .every((selected) => selected);

    for (let i = startIndex; i < endIndex; i++) {
      newSelectedRows[i] = !areAllSelected;
    }
    setSelectedRows(newSelectedRows);
  };

  const handleRowCheckboxChange = (index) => {
    const actualIndex = (currentPage - 1) * itemsPerPage + index;
    const newSelectedRows = [...selectedRows];
    newSelectedRows[actualIndex] = !newSelectedRows[actualIndex];
    setSelectedRows(newSelectedRows);
  };

  const handleDepartmentChange = (selectedOption) => {
    setSelectedDepartment(selectedOption);
    setCurrentPage(1);
  };

  const departmentOptions = [
    { value: "all", label: "All" },
    ...(departments || []).map((dept) => ({ value: dept, label: dept })),
  ];

  const handleSortChange = (selectedOption) => {
    setSelectedSort(selectedOption);
    setCurrentPage(1);
  };

  const sortOptions = [
    { value: "default", label: "Default" },
    { value: "serialAsc", label: "Serial Number (Asc)" },
    { value: "serialDesc", label: "Serial Number (Desc)" },
    { value: "experienceAsc", label: "Experience (Asc)" },
    { value: "experienceDesc", label: "Experience (Desc)" },
  ];

  const filteredData =
    selectedDepartment?.value === "all" || !selectedDepartment
      ? data
      : data.filter((doctor) => {
          const department =
            doctor.translations?.[
              selectedLanguage
            ]?.department?.toLowerCase() || "";
          return department === selectedDepartment.value.toLowerCase();
        });

  const sortedData = () => {
    let sorted = [...filteredData];
    if (!selectedSort || selectedSort.value === "default") {
      return sorted.sort((a, b) => {
        const indexA = additionOrder.indexOf(a.id);
        const indexB = additionOrder.indexOf(b.id);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
    }

    if (selectedSort.value === "serialAsc") {
      sorted.sort((a, b) => {
        const idA = a.id || "";
        const idB = b.id || "";
        return idA.localeCompare(idB);
      });
    } else if (selectedSort.value === "serialDesc") {
      sorted.sort((a, b) => {
        const idA = a.id || "";
        const idB = b.id || "";
        return idB.localeCompare(idA);
      });
    } else if (selectedSort.value === "experienceAsc") {
      sorted.sort((a, b) => {
        const expA =
          parseInt(a.translations[selectedLanguage]?.yearsOfExperience, 10) ||
          0;
        const expB =
          parseInt(b.translations[selectedLanguage]?.yearsOfExperience, 10) ||
          0;
        return expA - expB;
      });
    } else if (selectedSort.value === "experienceDesc") {
      sorted.sort((a, b) => {
        const expA =
          parseInt(a.translations[selectedLanguage]?.yearsOfExperience, 10) ||
          0;
        const expB =
          parseInt(b.translations[selectedLanguage]?.yearsOfExperience, 10) ||
          0;
        return expB - expA;
      });
    }
    return sorted;
  };

  const finalData = sortedData();
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = currentPage * itemsPerPage;
  const currentData = finalData.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleAddDoctor = async () => {
    setIsLoading(true);
    try {
      await handleAdd();
      setSelectedSort({ value: "default", label: "Default" });
      setSelectedDepartment({ value: "all", label: "All" });
      const newTotalPages = Math.ceil((data.length + 1) / itemsPerPage);
      setCurrentPage(newTotalPages);
      toast.success("Doctor added successfully!");
    } catch (error) {
      console.error("Add doctor error:", error);
      toast.error("Failed to add doctor.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatConsultationTime = (schedule) => {
    if (!Array.isArray(schedule) || schedule.length === 0) {
      return "No schedule available";
    }

    const sortedDays = [
      "Saturday",
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
    ];

    // Normalize day string
    const normalizeDay = (day) => {
      if (typeof day !== "string") return null;
      const lowerDay = day.toLowerCase();
      return sortedDays.find((d) => d.toLowerCase() === lowerDay) || null;
    };

    // Convert time to 24-hour format
    const convertTo24Hour = (time) => {
      if (typeof time !== "string" || time.trim() === "") {
        return null;
      }

      if (/^\d{2}:\d{2}$/.test(time)) {
        const [hours, minutes] = time.split(":").map(Number);
        if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
          return `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}`;
        }
        return null;
      }

      if (!time.includes(" ")) {
        return null;
      }

      const [timePart, period] = time.split(" ");
      if (
        !timePart ||
        !period ||
        !["AM", "PM"].includes(period.toUpperCase())
      ) {
        return null;
      }

      const timeParts = timePart.split(":");
      if (timeParts.length !== 2) {
        return null;
      }

      const [hoursStr, minutesStr] = timeParts;
      const hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);

      if (isNaN(hours) || isNaN(minutes)) {
        console.warn("Invalid hours or minutes:", time);
        return null;
      }

      let adjustedHours = hours;
      if (period.toUpperCase() === "PM" && hours !== 12) adjustedHours += 12;
      if (period.toUpperCase() === "AM" && hours === 12) adjustedHours = 0;

      return `${adjustedHours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    };

    // Normalize and filter valid schedule entries
    const normalizedSchedule = schedule
      .map((item, index) => {
        if (!item || typeof item !== "object") {
          console.warn(`Invalid schedule item at index ${index}:`, item);
          return null;
        }

        const normalizedDay = normalizeDay(item.day);
        if (!normalizedDay) {
          console.warn(
            `Invalid or unrecognized day in item at index ${index}:`,
            item.day
          );
          return null;
        }

        const startTime24 = convertTo24Hour(item.startTime);
        const endTime24 = convertTo24Hour(item.endTime);
        if (!startTime24 || !endTime24) {
          console.warn(`Invalid times in item at index ${index}:`, item);
          return null;
        }

        return {
          day: normalizedDay,
          startTime: item.startTime,
          endTime: item.endTime,
          startTime24,
          endTime24,
        };
      })
      .filter((item) => item !== null);

    if (normalizedSchedule.length === 0) {
      return "No valid schedule available";
    }

    // Sort by day order
    normalizedSchedule.sort(
      (a, b) => sortedDays.indexOf(a.day) - sortedDays.indexOf(b.day)
    );

    // Group days by startTime and endTime
    const timeGroups = {};
    normalizedSchedule.forEach((item) => {
      const key = `${item.startTime}-${item.endTime}`;
      if (!timeGroups[key]) {
        timeGroups[key] = [];
      }
      timeGroups[key].push(item);
    });

    // Find the group with the most days
    let largestGroup = [];
    Object.values(timeGroups).forEach((group) => {
      if (group.length > largestGroup.length) {
        largestGroup = group;
      }
    });

    if (largestGroup.length === 0) {
      return "No valid schedule available";
    }

    // Format the largest group as ranges
    const days = largestGroup.map((item) => item.day);
    const dayIndices = days.map((day) => sortedDays.indexOf(day));
    let ranges = [];
    let start = dayIndices[0];
    let prev = dayIndices[0];

    for (let i = 1; i <= dayIndices.length; i++) {
      if (i === dayIndices.length || dayIndices[i] !== prev + 1) {
        if (start === prev) {
          ranges.push(sortedDays[start].substring(0, 3));
        } else {
          ranges.push(
            `${sortedDays[start].substring(0, 3)}-${sortedDays[prev].substring(
              0,
              3
            )}`
          );
        }
        if (i < dayIndices.length) {
          start = dayIndices[i];
        }
      }
      prev = dayIndices[i] || prev;
    }

    const time = `${largestGroup[0].startTime24} - ${largestGroup[0].endTime24}`;
    return `${ranges.join(", ")}: ${time}`;
  };

  const handleDeleteSelectedRows = () => {
    const selectedDoctorIds = finalData
      .filter(
        (_, index) => selectedRows[(currentPage - 1) * itemsPerPage + index]
      )
      .map((doctor) => doctor.id);

    if (selectedDoctorIds.length > 0) {
      openDeleteModal(selectedDoctorIds);
    } else {
      toast.error("No doctor(s) selected.");
    }
  };

  return (
    <div>
      <div className="bg-white shadow-sm rounded-lg relative">
        {(isLoading || loading) && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-50 flex items-center justify-center z-10">
            <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12"></div>
          </div>
        )}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 gap-y-2 lg:gap-y-0 lg:gap-2">
          <div className="inline-flex items-center gap-3">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Doctor List ({data.length} doctors)
            </h2>
            {selectedRows.some((isSelected) => isSelected) && (
              <button
                onClick={handleDeleteSelectedRows}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                disabled={isLoading || loading}
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="inline-flex flex-col flex-wrap sm:flex-row lg:items-center lg:justify-end gap-3 flex-1">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by name, email, or department"
              value={searchTerm}
              onChange={handleSearch}
              disabled={isLoading || loading}
            />
            <div className="w-full md:w-48 h-10">
              <DynamicSelect
                options={departmentOptions}
                onChange={handleDepartmentChange}
                value={selectedDepartment}
                placeholder="Select Department"
                isSearchable
                isDisabled={isLoading || loading}
              />
            </div>
            <div className="w-full md:w-48 h-10">
              <DynamicSelect
                options={sortOptions}
                onChange={handleSortChange}
                value={selectedSort}
                placeholder="Sort By"
                isSearchable
                isDisabled={isLoading || loading}
              />
            </div>
            <button
              className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              disabled={isLoading || loading}
              onClick={handleAddDoctor}
            >
              + Add Doctor
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[1440px] w-full table-auto divide-y divide-gray-200">
            <thead>
              <tr className="px-3 bg-[#FBFBFD]">
                <th className="text-left">
                  <div className="relative left-3 -top-[3px]">
                    <input
                      type="checkbox"
                      id="agreement"
                      className="hidden peer"
                      checked={isHeaderChecked}
                      onChange={handleHeaderCheckboxChange}
                      disabled={isLoading || loading}
                    />
                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                      <FaCheck className="text-slate-100" />
                    </span>
                    <label
                      htmlFor="agreement"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                    ></label>
                  </div>
                </th>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3"
                  >
                    {column}
                  </th>
                ))}
                <th className="text-center uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton
                  rowCount={itemsPerPage}
                  columns={skeletonColumns}
                />
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 2} className="text-center py-5">
                    No doctors found.
                  </td>
                </tr>
              ) : (
                currentData.map((doctor, index) => {
                  const serialNumber = indexOfFirstItem + index + 1;
                  return (
                    <tr
                      key={doctor.id}
                      className={`border-b border-M-text-color/10 hover:bg-gray-100`}
                    >
                      <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color">
                        <div className="relative left-3 -top-[3px]">
                          <input
                            type="checkbox"
                            id={`doc${index}`}
                            className="hidden peer"
                            checked={
                              selectedRows[indexOfFirstItem + index] || false
                            }
                            onChange={() => handleRowCheckboxChange(index)}
                            disabled={isLoading || loading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={`doc${index}`}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                          ></label>
                        </div>
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-2 pl-5">
                        {serialNumber}
                      </td>
                      <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color py-5 px-2">
                        <div className="flex items-center gap-2">
                          <img
                            src={`https://api.muktihospital.com/${doctor.icon}`}
                            alt={doctor.translations[selectedLanguage]?.name}
                            className="size-8 rounded-full border border-M-text-color/20 shrink-0"
                            onError={(e) =>
                              (e.target.src = "/path/to/placeholder.png")
                            }
                          />
                          {doctor.translations[selectedLanguage]?.name || "N/A"}
                        </div>
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-2">
                        {doctor.translations[selectedLanguage]?.department ||
                          "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-2">
                        {doctor.translations[selectedLanguage]
                          ?.contactNumberSerial || "N/A"}
                      </td>
                      <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-2">
                        {doctor.email || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-2">
                        {formatConsultationTime(doctor.schedule)}
                      </td>
                      <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-2">
                        {doctor.translations[selectedLanguage]
                          ?.yearsOfExperience + " years" || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-2">
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to={`https://muktihospital.com/doctor/${doctor.slug}`}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            title="View"
                          >
                            <FaRegEye />
                          </Link>
                          <button
                            onClick={() => handleEdit(doctor.id)}
                            className="size-7 rounded-full bg-M-Green-color/10 text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                            disabled={isLoading || loading}
                            title="Edit"
                          >
                            <FaRegEdit />
                          </button>
                          <button
                            onClick={() => openDeleteModal([doctor.id])}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            disabled={isLoading || loading}
                            title="Delete"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={finalData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default DoctorTable;

